'use client';

import { useCallback, useEffect, useState } from 'react';

import { addReplyAPI, fetchReplies } from './clip-reply-apis';
import type { BasicUserProfileInfo, CommentMessage } from './types';

type UseRepliesHook = {
  replies: CommentMessage[];
  addReply: (
    text: string,
    userId: string,
    userMeta: BasicUserProfileInfo | null,
  ) => Promise<void>;
  retryReply: (replyId: string) => void;
  deleteReply: (replyId: string) => void;
  fetchMoreReplies: () => Promise<void>;
  isReplyLoading: boolean;
  isFetchingMoreReplies: boolean;
  hasMoreReplies: boolean;
};

export function useClipReplies(
  commentId: string,
  clipId: string,
  commentTimestamp: number,
  hasReplies: boolean,
): UseRepliesHook {
  const [replies, setReplies] = useState<CommentMessage[]>([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<{
    clip_id: string;
    comment_id: string;
    customer_id: string;
  } | null>(null);
  const [isReplyLoading, setIsReplyLoading] = useState(false);
  const [isFetchingMoreReplies, setIsFetchingMoreReplies] = useState(false);
  const [hasMoreReplies, setHasMoreReplies] = useState(false);

  useEffect(() => {
    if (!hasReplies) return;
    let cancelled = false;

    async function loadReplies() {
      try {
        const response = await fetchReplies(commentId, clipId, 20, null);
        if (!cancelled) {
          setReplies(response.replies);
          setLastEvaluatedKey(response.lastEvaluatedKey);
          setHasMoreReplies(!!response.lastEvaluatedKey);
        }
      } catch (error) {
        console.error('Error fetching replies:', error);
      }
    }

    void loadReplies();
    return () => {
      cancelled = true;
    };
  }, [commentId, clipId, hasReplies]);

  const fetchMoreReplies = useCallback(async () => {
    if (!lastEvaluatedKey || isFetchingMoreReplies) return;
    setIsFetchingMoreReplies(true);
    try {
      const response = await fetchReplies(
        commentId,
        clipId,
        20,
        lastEvaluatedKey,
      );
      setReplies(prev => {
        const existingIds = new Set(prev.map(r => r.id));
        const newReplies = response.replies.filter(r => !existingIds.has(r.id));
        return [...prev, ...newReplies];
      });
      setLastEvaluatedKey(response.lastEvaluatedKey);
      setHasMoreReplies(!!response.lastEvaluatedKey);
    } catch (error) {
      console.error('Error fetching more replies:', error);
    } finally {
      setIsFetchingMoreReplies(false);
    }
  }, [commentId, clipId, lastEvaluatedKey, isFetchingMoreReplies]);

  const addReply = useCallback(
    async (
      text: string,
      userId: string,
      userMeta: BasicUserProfileInfo | null,
    ) => {
      setIsReplyLoading(true);
      const tempId = Date.now().toString();

      const newReply: CommentMessage = {
        type: 'reply',
        commentId,
        id: tempId,
        username: userMeta?.username,
        userId,
        text,
        avatar: userMeta?.avatar,
        replyCount: 0,
        status: 'uploading',
        name: userMeta?.fullName || '',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        timestamp: Date.now(),
      };

      setReplies(prev => [...prev, newReply]);

      try {
        const result = await addReplyAPI(
          commentId,
          clipId,
          commentTimestamp,
          text,
          userId,
          userMeta,
        );
        setReplies(prev =>
          prev.map(r =>
            r.id === tempId ? { ...r, ...result, status: 'added' as const } : r,
          ),
        );
      } catch {
        setReplies(prev =>
          prev.map(r =>
            r.id === tempId ? { ...r, status: 'failed' as const } : r,
          ),
        );
      }

      setIsReplyLoading(false);
    },
    [commentId, clipId, commentTimestamp],
  );

  const retryReply = useCallback((replyId: string) => {
    setReplies(prev =>
      prev.map(r =>
        r.id === replyId ? { ...r, status: 'uploading' as const } : r,
      ),
    );
  }, []);

  const deleteReply = useCallback((replyId: string) => {
    setReplies(prev =>
      prev.map(r => (r.id === replyId ? { ...r, isDeleted: true } : r)),
    );
  }, []);

  return {
    replies,
    addReply,
    retryReply,
    deleteReply,
    fetchMoreReplies,
    isReplyLoading,
    isFetchingMoreReplies,
    hasMoreReplies,
  };
}
